import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { type LossRunClaim, usePatchLossRunClaim } from '@/models/lossRunRequest'
import { ClaimFields } from './ClaimFields'
import { claimFormError, type ClaimFormState, claimFormToPayload, claimToForm } from './claimForm'

interface EditClaimDialogProps {
  claim: LossRunClaim
  providerName: string
  coveragePeriod: string
  orgId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function EditClaimDialog({
  claim,
  providerName,
  coveragePeriod,
  orgId,
  open,
  onOpenChange,
}: EditClaimDialogProps) {
  const { mutateAsync: patchClaim, isPending } = usePatchLossRunClaim(orgId)
  const [form, setForm] = useState<ClaimFormState>(() => claimToForm(claim))

  function handleOpenChange(next: boolean) {
    if (next) setForm(claimToForm(claim))
    onOpenChange(next)
  }

  async function handleSave() {
    const problem = claimFormError(form)
    if (problem) {
      toast.error(problem)
      return
    }
    try {
      await patchClaim({ claimId: claim.id, data: claimFormToPayload(form) })
      toast.success('Claim updated')
      onOpenChange(false)
    } catch {
      toast.error('Failed to save changes')
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Edit claim</DialogTitle>
          <DialogDescription>
            {providerName} · {coveragePeriod}
          </DialogDescription>
        </DialogHeader>
        <ClaimFields value={form} onChange={setForm} disabled={isPending} />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={() => void handleSave()} loading={isPending ? 'Saving…' : undefined}>
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
